import React, { useState } from 'react'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import Link from 'next/link'
import { useRouter } from 'next/router'
import Menu from './Menu'
import { withTranslation } from '~/i18n'
import {
  Logo,
  Button,
  Avatar,
  space,
  color,
  fontSize,
  device,
  transition,
  easing,
  radius,
  shadow,
  useKeyPress,
  fontWeight,
  useTransition,
  useOnClickOutside,
} from '@ticketswap/solar'
import Overlay from './Overlay'
import AuthDialog from '../AuthDialog'
import SearchOverlay from '../SearchOverlay'
import { useViewer } from '../ViewerContext'
import { SELL_BUTTON_CLICKED, AUTHENTICATION_VIEWED } from '~/lib/tracking-events'
import { track, getLinkProps } from '../../utils'
import { MagnifyingGlassAlt, Cart, CloseAlt, ListAlt, ChevronDown } from '@ticketswap/comets'
import { BecomePartnerButton } from '~/components/BlobCover/PartnerCover'
import PropTypes from 'prop-types'
import { VOUCHER_SELL_ENABLED } from '~/lib/tracking-events'

const duration = 200

export const MenubarContainer = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  z-index: 6;
`

const StyledLogo = styled(Logo)`
  color: ${props => (props.logo === 'blue' || props.showNavOverlay ? color.earth : color.nova)};

  [data-theme='dark'] & {
    color: ${color.space};
  }
`

const Container = styled.div`
  position: relative;
  color: ${color.nova};
  padding: ${space[16]};
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${props =>
    props.backgroundImageUrl &&
    css`
      background-color: ${color.space};

      &::before {
        content: '';
        opacity: 0.25;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        position: absolute;
        z-index: 0;
        background-image: url(${props.backgroundImageUrl});
        background-size: cover;
        background-position: center;

        [data-theme='dark'] & {
          background-image: unset;
          background-color: ${color.nova};
          opacity: 1;
        }
      }
    `};

  @media ${device.tablet} {
    position: 'relative';
  }

  @media ${device.tablet} {
    padding: ${space[24]} ${space[32]};

    &::after {
      display: none;
    }
  }
`

const Nav = styled.nav`
  position: relative;
`

const NavList = styled.ul`
  position: relative;
  z-index: 4;
  display: flex;
  align-items: center;
`

const NavItem = styled.li`
  display: ${props => props.display};

  margin-left: ${space[16]};

  @media ${device.tablet} {
    display: ${props => props.displayMd};

    margin-left: ${space[32]};
  }

  &:first-of-type {
    margin-left: 0;
  }
`

const HomeLink = styled.a`
  line-height: 0;
  position: relative;
  z-index: 4;
  outline: 0;
  transition: color ${transition};

  .light & {
    color: ${color.nova};

    [data-theme='dark'] & {
      color: ${color.space};
    }
  }
  .dark &,
  .light.has-overlay & {
    color: ${color.earth};
  }

  @media ${device.tablet} {
    .light & {
      color: ${color.nova} !important;

      [data-theme='dark'] & {
        color: ${color.space} !important;
      }
    }
    .dark & {
      color: ${color.earth};
    }
  }
`

const NavLink = styled.a`
  color: ${props => (props.theme === 'light' ? 'white' : color.spaceLight)};
  font-size: ${fontSize[18]};
  font-weight: ${fontWeight.semiBold};
  transition: color ${transition};
  outline: 0;
  position: relative;

  .light & {
    color: ${color.nova};

    [data-theme='dark'] & {
      color: ${color.space};
    }
  }
  .dark &,
  .light.has-overlay & {
    color: ${color.spaceLight};
  }

  @media ${device.tablet} {
    .light & {
      color: ${color.nova} !important;

      [data-theme='dark'] & {
        color: ${color.space} !important;
      }
    }
    .dark & {
      color: ${color.spaceLight};
    }
  }
`

const MenuDropdownWrapper = styled.div`
  position: relative;
`

const MenuDropdownContainer = styled.div`
  opacity: ${props => (props.state === 'entering' || props.state === 'entered' ? 1 : 0)};
  transform: ${props => {
    switch (props.state) {
      case 'exited':
        return 'translate3d(0,-1rem,0) scale(0.5)'
      case 'exiting':
        return 'translate3d(0,0rem,0) scale(1)'
      default:
        return 'translate3d(0,0,0) scale(1)'
    }
  }};
  transition-duration: ${duration}ms;
  transition-timing-function: ${easing.easeOutCubic};
  transition-property: opacity, transform;
`

const MenuDropdown = styled.ul`
  position: absolute;
  z-index: 2;
  padding: ${space[16]};
  left: 50%;
  top: calc(100% ${props => (props.distance ? ` + ${space[props.distance]}` : null)});
  transform: translateX(-50%);
  border-radius: ${radius.lg};
  display: block;
  box-shadow: ${shadow.strong};
  background-color: ${color.nova};
  color: ${color.nova};

  [data-theme='dark'] & {
    border: 1px solid ${color.spaceLightest};
  }
`

const LittleArrow = styled.span`
  display: inline-block;
  margin-left: ${space[4]};

  .light & {
    color: ${color.nova};

    [data-theme='dark'] & {
      color: ${color.space};
    }
  }
  .dark &,
  .light.has-overlay & {
    color: ${color.spaceLight};
  }

  @media ${device.tablet} {
    .light & {
      color: ${color.nova} !important;

      [data-theme='dark'] & {
        color: ${color.space} !important;
      }
    }
    .dark & {
      color: ${color.spaceLight};
    }
  }
`

const AccountAlertBadge = styled.span`
  height: ${space[12]};
  width: ${space[12]};
  border-radius: 50%;
  background-color: ${color.mars};
  display: block;
  position: absolute;
  top: 0;
  right: 0;

  @media ${device.tablet} {
    height: ${space[16]};
    width: ${space[16]};
    right: auto;
    left: ${space[32]};
    top: -${space[4]};
  }
`

function Menubar({ t, appearance, hasSearchToggle, type, scroll, logo, ...props }) {
  const router = useRouter()
  const { pathname = '' } = router || {}
  const [showNavOverlay, setShowNavOverlay] = useState(false)
  const [showNavMenu, setShowNavMenu] = useState(false)
  const [avatarClicked, setAvatarClicked] = useState(0)
  const { viewer } = useViewer()
  const [state, mounted, transitioning] = useTransition({
    in: showNavMenu,
    timeout: duration,
  })
  const esc = useKeyPress('Escape')
  const ref = React.useRef()
  useOnClickOutside(ref, () => setShowNavMenu(false))

  React.useEffect(() => {
    // Close menu with Esc key
    if (showNavMenu && esc && !transitioning) setShowNavMenu(false)
  }, [showNavMenu, esc, transitioning])

  function toggleNavOverlay() {
    return setShowNavOverlay(!showNavOverlay)
  }

  return (
    <Container {...props} className={`${appearance}${showNavOverlay ? ' has-overlay' : ''}`}>
      <Link href="/" passHref>
        <HomeLink aria-label={t('common:menubar.home')}>
          <StyledLogo logo={logo} showNavOverlay={showNavOverlay} />
        </HomeLink>
      </Link>

      <Nav>
        <NavList>
          {hasSearchToggle && (
            <NavItem>
              <SearchOverlay>
                {({ getToggleProps }) => (
                  <NavLink as="button" {...getToggleProps()}>
                    <MagnifyingGlassAlt />
                  </NavLink>
                )}
              </SearchOverlay>
            </NavItem>
          )}

          <>
            <NavItem display="block" displayMd="none">
              <Link {...getLinkProps('CART')} passHref>
                <NavLink aria-label={t('common:menubar.cart')}>
                  <Cart />
                </NavLink>
              </Link>
            </NavItem>
            <NavItem display="block" displayMd="none">
              <NavLink
                as="button"
                onClick={toggleNavOverlay}
                data-testid="menu-overlay-toggle"
                aria-label={t('common:menubar.menuOverlay')}
              >
                {showNavOverlay ? (
                  <CloseAlt />
                ) : (
                  <>
                    <ListAlt />
                    {viewer?.accountAlerts?.length > 0 && <AccountAlertBadge />}
                  </>
                )}
              </NavLink>
            </NavItem>
            {type === 'fundraising' ? (
              <>
                <NavItem display="none" displayMd="block">
                  <NavLink href="/blog">{t('common:generic.blog')}</NavLink>
                </NavItem>

                <NavItem display="none" displayMd="block">
                  <NavLink href="/content/about-us">{t('common:menubar.about-us')}</NavLink>
                </NavItem>
              </>
            ) : type !== 'partner' ? (
              <>
                {!viewer?.id && (
                  <NavItem display="none" displayMd="block">
                    <NavLink href="/how-does-it-work">{t('common:menubar.howItWorks')}</NavLink>
                  </NavItem>
                )}

                {viewer?.id && (
                  <>
                    <NavItem display="none" displayMd="block">
                      <Link {...getLinkProps('CART')} passHref>
                        <NavLink>{t('common:menubar.cart')}</NavLink>
                      </Link>
                    </NavItem>

                    <NavItem display="none" displayMd="block">
                      <Link {...getLinkProps('HELP')} passHref>
                        <NavLink>{t('common:menubar.help')}</NavLink>
                      </Link>
                    </NavItem>
                  </>
                )}
              </>
            ) : (
              <>
                <NavItem display="none" displayMd="block">
                  <NavLink href="/blog">{t('common:generic.blog')}</NavLink>
                </NavItem>

                <NavItem display="none" displayMd="block">
                  <NavLink href="/content/secure-swap">{t('common:menubar.secureSwap')}</NavLink>
                </NavItem>
              </>
            )}
            <NavItem display="none" displayMd="block">
              <AuthDialog onToggle={on => on && track(AUTHENTICATION_VIEWED)}>
                {({ getToggleProps }) => (
                  <>
                    {viewer?.id && (
                      <MenuDropdownWrapper ref={ref}>
                        <NavLink
                          as="button"
                          theme="light"
                          onClick={() => setShowNavMenu(!showNavMenu)}
                        >
                          <Avatar
                            src={viewer.avatar}
                            size={44}
                            onClick={() => setAvatarClicked(avatarClicked + 1)}
                          />
                          {viewer?.accountAlerts?.length > 0 && <AccountAlertBadge />}
                          <LittleArrow>
                            <ChevronDown size={16} />
                          </LittleArrow>
                        </NavLink>

                        {mounted && (
                          <MenuDropdownContainer state={state}>
                            <MenuDropdown distance={16}>
                              <Menu t={t} />
                            </MenuDropdown>
                          </MenuDropdownContainer>
                        )}
                      </MenuDropdownWrapper>
                    )}

                    {!viewer?.id && (
                      <NavLink as="button" {...getToggleProps()}>
                        {t('common:menubar.login')}
                      </NavLink>
                    )}
                  </>
                )}
              </AuthDialog>
            </NavItem>
          </>

          <NavItem display="none" displayMd="block">
            {type === 'default' && (
              <Link href="/sell" passHref>
                <Button
                  as="a"
                  variant="success"
                  size="medium"
                  onClick={() => {
                    track(SELL_BUTTON_CLICKED, {
                      pathname,
                    })
                  }}
                >
                  {t('common:menubar.sellTickets')}
                </Button>
              </Link>
            )}

            {type === 'vouchers' && (
              <Link {...getLinkProps('VOUCHERS_SELL')} passHref>
                <Button
                  as="a"
                  variant="success"
                  size="medium"
                  onClick={() => {
                    track(VOUCHER_SELL_ENABLED, {
                      source: 'header',
                    })
                  }}
                >
                  {t('common:menubar.sellVouchers')}
                </Button>
              </Link>
            )}

            {type === 'partner' && (
              <BecomePartnerButton type="default" variant="inverted" size="medium" t={t} />
            )}

            {type === 'fundraising' && (
              <Button type="default" variant="inverted" size="medium" onClick={scroll}>
                {t('fundraising:header.button.signup')}
              </Button>
            )}
          </NavItem>
        </NavList>

        {showNavOverlay && <Overlay t={t} type={type} />}
      </Nav>
    </Container>
  )
}

Menubar.defaultProps = {
  appearance: 'dark',
  hasSearchToggle: true,
  type: 'default',
  scroll: () => null,
}

Menubar.propTypes = {
  t: PropTypes.func.isRequired,
  appearance: PropTypes.oneOf(['dark', 'light']).isRequired,
  hasSearchToggle: PropTypes.bool.isRequired,
  type: PropTypes.oneOf(['default', 'partner', 'vouchers', 'fundraising']),
  logo: PropTypes.oneOf(['blue', 'black', 'white']),
  scroll: PropTypes.func,
}

export default withTranslation('common')(Menubar)
